import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FormattedPageTitle} from "@/components/pages";
import UpdateExtensionForm from "./UpdateExtensionForm";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {useFormGuard} from "@/utils/formGuard";
import Loading from "../../breadcrumbs/Loading";
import UpdateExtensionFieldList from "./UpdateExtensionFieldList";
import {CustomWidgetRenderers, FieldName} from "./types";
import ResyncDialog, {ResyncDialogHandler} from "../ResyncDialog";
import {DomainOutgoingCallBarrings, useBoolState} from "@/hooks";
import {formPagesWidth} from "@/constants";
import {
    FirstStageQueryResult,
    SecondStageQueryResult,
    useFirstStageLazyQuery,
    useSecondStageLazyQuery,
} from "./queries.generated";
import {createInitialData} from "./initialData";
import {DomainType, UpdateUserData} from "@/api";
import {ContextFilters} from "@/context/AuthProvider";
import {defineMessage} from "react-intl";
import {useDomainOutgoingCallBarringsLazyQuery} from "@/queries.generated";

type Extension = FirstStageQueryResult["data"]["users"]["items"][number];

type ContentProps = {
    id: number;
    fields: FieldName[];
    customWidgetRenderers?: CustomWidgetRenderers;
    extension: Extension;
    secondStageData: SecondStageQueryResult["data"];
    domainCallBarrings: DomainOutgoingCallBarrings;
    initialData: UpdateUserData;
    refetch?: () => Promise<void>;
};

const Content = ({
    id,
    fields,
    customWidgetRenderers,
    extension,
    secondStageData,
    domainCallBarrings,
    initialData,
    refetch,
}: ContentProps) => {
    const isResidential = extension.number.domain.domainType === DomainType.Residential;
    const resyncDialogRef = useRef<ResyncDialogHandler>(null);
    const [enabled, enable, disable] = useBoolState(true);
    const extensionContextFilter = ContextFilters.extension(extension.number.shortNumber, extension.number.domain.name);

    const form = new UpdateExtensionForm(id, initialData, isResidential, extensionContextFilter);
    useFormGuard(form.hasChanged);

    // FIXME: if we do not have this, whenever we submit the changes, the form wilé think the previous initial data
    // is the current initial data as well, therefore enabling 'reset' and 'submit' buttons
    useEffect(() => {
        form.newInitialData(initialData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    async function submit() {
        disable();
        if (await form.submit()) {
            await refetch();
            if (form.result.data.updateUser.devicesToResync.length) resyncDialogRef.current.open();
        }
        enable();
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "extensionSettings", defaultMessage: "Settings for user {name}"})}
                values={{name: form.values.displayName}}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <UpdateExtensionFieldList
                    form={form}
                    fields={fields}
                    enabled={enabled}
                    isResidential={isResidential}
                    extension={extension}
                    groups={secondStageData?.groups.items ?? []}
                    domainCallBarrings={domainCallBarrings}
                    locations={secondStageData?.locations.items ?? []}
                    overrideCallerNumbers={secondStageData?.overrideNumbers ?? []}
                    secretaryNumbers={secondStageData?.secretaryNumbers ?? []}
                    customWidgetRenderers={customWidgetRenderers}
                    extensionContextFilter={extensionContextFilter}
                />
                <FormattedFormRow>
                    <div style={{margin: "1em 0"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                            enabled={form.hasChanged && enabled}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
            <ResyncDialog ref={resyncDialogRef} devicesToResync={form.result?.data.updateUser?.devicesToResync ?? []} />
        </div>
    );
};

type Props = {
    id: number;
    fields: FieldName[];
    customWidgetRenderers?: CustomWidgetRenderers;
    fetchSecondStage?: boolean;
};

const UpdateExtensionFormPage = ({id, fields, customWidgetRenderers, fetchSecondStage = true}: Props) => {
    const [firstStageQuery] = useFirstStageLazyQuery({fetchPolicy: "no-cache"});
    const [secondStageQuery] = useSecondStageLazyQuery({fetchPolicy: "no-cache"});
    const [domainCallBarringsQuery] = useDomainOutgoingCallBarringsLazyQuery({fetchPolicy: "no-cache"});

    const [data, setData] = useState<{
        firstStage: FirstStageQueryResult["data"];
        secondStage: SecondStageQueryResult["data"];
        domainCallBarrings: DomainOutgoingCallBarrings;
    }>(undefined);

    const fetch = useCallback(async () => {
        const firstStageResult = await firstStageQuery({variables: {extensionId: id}});

        const domainId = firstStageResult.data.users.items[0].number.domain.id;
        const {
            data: {
                domains: {items: barrings},
            },
        } = await domainCallBarringsQuery({
            variables: {domainId: domainId},
        });

        let secondStageResult = undefined;
        if (fetchSecondStage) {
            const extension = firstStageResult.data.users.items[0];
            secondStageResult = await secondStageQuery({
                variables: {domainId: extension.number.domain.id, numberId: extension.numberId},
            });
        }

        setData({
            firstStage: firstStageResult.data,
            secondStage: secondStageResult?.data,
            domainCallBarrings: {
                operator: barrings[0]?.outgoingCallBarringOperator ?? [],
                admin: barrings[0]?.outgoingCallBarringAdmin ?? [],
            },
        });
    }, [firstStageQuery, id, domainCallBarringsQuery, fetchSecondStage, secondStageQuery]);

    const initialData = useMemo(() => {
        return createInitialData(data?.firstStage?.users?.items[0], data?.secondStage?.groups?.items);
    }, [data]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    if (data?.firstStage === undefined) return <Loading />;

    return (
        <Content
            id={id}
            fields={fields}
            customWidgetRenderers={customWidgetRenderers}
            extension={data.firstStage.users.items[0]}
            secondStageData={data.secondStage}
            domainCallBarrings={data.domainCallBarrings}
            initialData={initialData}
            refetch={fetch}
        />
    );
};

export default UpdateExtensionFormPage;
